import type { PoiView, RouteMode, RouteStop } from './api.js';
import { appleMapsLocateUrl, googleMapsPlaceUrl } from './routing.js';
import { haversineDistanceM } from './group-actions.js';

/** Canonical label for "Add to route" affordances across the app
 *  (POICard, MapPopup, SurpriseReveal, PoiDetailSheet). Kept here so
 *  the wording stays in lockstep — four surfaces shared the same
 *  ternary literal before this was extracted. */
export function routeActionLabel(state: {
  inRoute: boolean;
  routeEmpty: boolean;
}): string {
  if (state.inRoute) return 'In your route';
  return state.routeEmpty ? 'Route' : 'Add to route';
}

interface Coord {
  lat: number;
  lng: number;
}

interface RoutePlan {
  /** null = omit (Maps defaults to current location). */
  origin: Coord | null;
  destination: Coord;
  waypoints: Coord[];
}

/**
 * Resolve route stops into the {origin, destination, waypoints} the
 * Maps URL schemes expect. Returns null when the route has nothing the
 * Maps app can navigate to — empty route, or "here" only with no POI.
 *
 * POIs that no longer exist (e.g. deleted while their id was still in
 * the route) are silently dropped. The caller decides whether to surface
 * that to the user.
 */
function planRoute(
  stops: RouteStop[],
  lookup: (poiId: number) => PoiView | null,
): RoutePlan | null {
  const resolved: (Coord | 'here')[] = [];
  for (const s of stops) {
    if (s.kind === 'here') {
      resolved.push('here');
    } else {
      const poi = lookup(s.poiId);
      if (poi) resolved.push({ lat: poi.lat, lng: poi.lng });
    }
  }

  if (resolved.length === 0) return null;

  // Last stop must be a real coordinate — Maps needs a destination.
  // "here" is constrained to position 0 by the UI, but be defensive.
  const last = resolved[resolved.length - 1]!;
  if (last === 'here') return null;

  // Single-POI route → "from current location to this POI".
  if (resolved.length === 1) {
    return { origin: null, destination: last, waypoints: [] };
  }

  const first = resolved[0]!;
  const origin = first === 'here' ? null : first;
  const middle = resolved.slice(1, -1);
  // "here" is only allowed at index 0; filter any others defensively.
  const waypoints = middle.filter((m): m is Coord => m !== 'here');

  return { origin, destination: last, waypoints };
}

function fmt(c: Coord): string {
  return `${c.lat},${c.lng}`;
}

/** True when the route is a single POI with no "here" — locate mode
 *  (show the place on the map) rather than directions mode. */
export function isLocateOnly(stops: RouteStop[]): boolean {
  return stops.length === 1 && stops[0]!.kind === 'poi';
}

/** Build a Google Maps URL for the given route + mode.
 *  - 1 POI alone (no "here"): locate URL (place page).
 *  - Otherwise: multi-stop directions URL with origin/waypoints/destination.
 *  Up to 9 waypoints; the route panel UI caps stops before that. */
export function buildGoogleMapsUrl(
  stops: RouteStop[],
  mode: RouteMode,
  lookup: (poiId: number) => PoiView | null,
): string | null {
  if (isLocateOnly(stops)) {
    const stop = stops[0]!;
    if (stop.kind !== 'poi') return null;
    const poi = lookup(stop.poiId);
    return poi ? googleMapsPlaceUrl(poi) : null;
  }

  const plan = planRoute(stops, lookup);
  if (!plan) return null;

  const params = new URLSearchParams();
  params.set('api', '1');
  if (plan.origin) params.set('origin', fmt(plan.origin));
  params.set('destination', fmt(plan.destination));
  if (plan.waypoints.length > 0) {
    params.set('waypoints', plan.waypoints.map(fmt).join('|'));
  }
  params.set('travelmode', mode);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Build an Apple Maps URL.
 *  - 1 POI alone (no "here"): locate URL (drop a pin).
 *  - Otherwise: unified /directions URL with source/waypoint/destination. */
export function buildAppleMapsUrl(
  stops: RouteStop[],
  mode: RouteMode,
  lookup: (poiId: number) => PoiView | null,
): string | null {
  if (isLocateOnly(stops)) {
    const stop = stops[0]!;
    if (stop.kind !== 'poi') return null;
    const poi = lookup(stop.poiId);
    return poi ? appleMapsLocateUrl(poi) : null;
  }

  const plan = planRoute(stops, lookup);
  if (!plan) return null;

  const params = new URLSearchParams();
  if (plan.origin) params.set('source', fmt(plan.origin));
  for (const wp of plan.waypoints) {
    params.append('waypoint', fmt(wp));
  }
  params.set('destination', fmt(plan.destination));
  params.set('mode', mode);
  return `https://maps.apple.com/directions?${params.toString()}`;
}

/**
 * Reorder stops using a nearest-neighbor TSP heuristic. The "here" stop
 * (if present) stays anchored at index 0. The first POI becomes the
 * anchor and the rest are appended in nearest-unvisited order.
 *
 * O(n²) — fine for n ≤ 20-ish, which is well past any realistic trip.
 */
export function bestOrder(
  stops: RouteStop[],
  lookup: (poiId: number) => PoiView | null,
): RouteStop[] {
  if (stops.length < 3) return stops;

  const hereAtStart = stops[0]!.kind === 'here';
  const tail = hereAtStart ? stops.slice(1) : stops;

  // Resolve POIs; drop unknown ids (POI was deleted).
  const resolved: { stop: RouteStop; poi: PoiView }[] = [];
  for (const s of tail) {
    if (s.kind === 'poi') {
      const poi = lookup(s.poiId);
      if (poi) resolved.push({ stop: s, poi });
    }
  }
  if (resolved.length < 2) return stops;

  const anchor = resolved[0]!;
  const remaining = resolved.slice(1);
  const ordered: typeof resolved = [anchor];
  let current = anchor.poi;

  while (remaining.length > 0) {
    let nearestIdx = 0;
    let nearestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineDistanceM(current, remaining[i]!.poi);
      if (d < nearestDist) {
        nearestDist = d;
        nearestIdx = i;
      }
    }
    const next = remaining.splice(nearestIdx, 1)[0]!;
    ordered.push(next);
    current = next.poi;
  }

  const orderedStops = ordered.map((r) => r.stop);
  return hereAtStart ? [stops[0]!, ...orderedStops] : orderedStops;
}
